import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// All requests send cookies automatically
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const location = useLocation();

  // Check existing session via httpOnly cookie
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API_URL}/auth/me`);
        setUser(response.data);
        setDarkMode(response.data.dark_mode || false);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  // Apply dark mode class — client-facing routes (/share, /print) always render light
  useEffect(() => {
    const clientRoute = location.pathname.startsWith('/share') || location.pathname.startsWith('/print');
    document.documentElement.classList.toggle('dark', darkMode && !clientRoute);
  }, [darkMode, location.pathname]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    setUser(response.data.user);
    setDarkMode(response.data.user.dark_mode || false);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API_URL}/auth/register`, { email, password, name });
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch {
      // Ignore errors on logout
    }
    setUser(null);
    setDarkMode(false);
    document.documentElement.classList.remove('dark');
  };

  const toggleDarkMode = useCallback(async () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    try {
      await axios.put(`${API_URL}/auth/preferences`, { dark_mode: newMode });
    } catch (err) {
      console.error('Failed to save dark mode preference:', err.message);
    }
  }, [darkMode]);

  // getAuthHeaders returns empty — cookies are sent automatically via withCredentials
  const getAuthHeaders = useCallback(() => ({}), []);

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    getAuthHeaders,
    darkMode,
    toggleDarkMode
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
